import { computed, inject, Injectable, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { catchError, map, Observable, of, tap, throwError } from 'rxjs';

import { AuthApiService, BranchesApiService, BranchResponseDto } from 'api';

import { AuthService } from '@auth/services/auth.service';
import { BranchSessionStore } from '@auth/services/branch-session.store';

/**
 * Rótulo de una sede cuyo nombre no se conoce (catálogo sin cargar, caído o con el nombre vacío).
 * Un id de sede es un dato interno: nunca se le muestra al usuario como si fuera el nombre.
 */
export const UNNAMED_BRANCH_LABEL = 'Sede sin nombre';

/** Sede accesible, ya resuelta a algo que se puede pintar en un menú. */
export interface BranchSessionOption {
  id: string;
  name: string;
  code: string;
  isDefault: boolean;
  isActive: boolean;
}

/**
 * Sede activa de la sesión: qué sedes puede usar el usuario y cómo cambia de una a otra.
 *
 * Vive en `auth/` porque **cambiar de sede es cambiar de token**, no un filtro de UI. El backend
 * emite el alcance como claims (`branch` / `branches`) y de ahí sale el filtro de las 17 entidades
 * acotadas por sede: si la UI mostrara una sede distinta de la del JWT, el usuario vería un rótulo
 * y unos datos que no se corresponden.
 *
 * Ver `DOCS/integration/FRONTEND_MULTI_SEDE_GUIDE.md` en el backend.
 */
@Injectable({ providedIn: 'root' })
export class BranchSessionService {
  private readonly authApi = inject(AuthApiService);
  private readonly branchesApi = inject(BranchesApiService);
  private readonly authService = inject(AuthService);
  private readonly store = inject(BranchSessionStore);

  private readonly _isSwitching = signal(false);

  /** Un cambio de sede en curso bloquea el selector: son dos tokens distintos en vuelo. */
  readonly isSwitching = computed(() => this._isSwitching());

  readonly activeBranchId = this.store.activeBranchId;
  readonly hasMultipleBranches = this.store.hasMultipleBranches;

  /**
   * El usuario está acotado por sede y no alcanza ninguna. Se reexpone aquí para que la barra
   * superior no tenga que hablar con `AuthService`: todo lo de la sesión de sede entra por este
   * servicio.
   */
  readonly hasNoReachableBranch = this.authService.hasNoReachableBranch;

  /**
   * Catálogo de sedes del tenant. Se pide una sola vez por sesión: son datos maestros que casi
   * nunca cambian, y el selector necesita el NOMBRE, mientras que el token solo trae ids.
   */
  private readonly catalogRs = rxResource({
    params: () => ({ tenantId: this.authService.user()?.tenantId ?? null }),
    stream: ({ params }) => {
      if (!params.tenantId) return of<BranchResponseDto[]>([]);
      return this.branchesApi.apiV1BranchesGet({ tenantId: params.tenantId }).pipe(
        map((r) => r.data ?? []),
        // Se publican los nombres en el store para que cualquier vista pueda resolver un
        // `branchId` suelto sin volver a pedir el catálogo ni depender de este servicio.
        tap((branches) =>
          this.store.setNames(
            new Map(branches.filter((b) => b.id && b.name?.trim()).map((b) => [b.id!, b.name!.trim()]))
          )
        ),
        // El selector es accesorio: si el catálogo falla, la sesión sigue siendo válida y las
        // sedes se rotulan «Sede sin nombre». Romper la barra superior por esto sería desproporcionado.
        catchError(() => of<BranchResponseDto[]>([]))
      );
    },
  });

  /**
   * Sedes entre las que el usuario puede moverse.
   *
   * El **token manda**: `branchIds` define a cuáles tiene acceso, y el catálogo solo aporta el
   * nombre. Hacerlo al revés dejaría elegir una sede que el backend va a rechazar con 403.
   */
  readonly branches = computed<BranchSessionOption[]>(() => {
    const accessible = this.store.branchIds();
    if (!accessible.length) return [];

    const byId = new Map((this.catalogRs.value() ?? []).map((b) => [b.id ?? '', b]));

    return accessible.map((id) => {
      const found = byId.get(id);
      return {
        id,
        // Sin catálogo cargado (o sin nombre) se rotula con un texto neutro: el id es un dato
        // interno y no se le muestra al usuario como nombre.
        name: found?.name?.trim() || UNNAMED_BRANCH_LABEL,
        code: found?.code ?? '',
        isDefault: found?.isDefault ?? false,
        isActive: found?.isActive ?? true,
      };
    });
  });

  /** La sede activa ya resuelta a nombre, para el rótulo de la barra superior. */
  readonly activeBranch = computed<BranchSessionOption | null>(() => {
    const active = this.store.activeBranchId();
    if (!active) return null;
    return this.branches().find((b) => b.id === active) ?? null;
  });

  /**
   * Cambia la sede activa y **reemplaza el access token** por el que devuelve el backend.
   *
   * El orden importa: primero se aplica el token nuevo y solo después la sede, para que ninguna
   * petición disparada por la actualización de la UI salga con el token viejo apuntando a la sede
   * nueva. El refresh token no se toca; al renovarlo conservará esta selección porque el backend
   * la persistió en el usuario.
   */
  changeActiveBranch(branchId: string): Observable<void> {
    if (!branchId || branchId === this.store.activeBranchId()) return of(void 0);

    this._isSwitching.set(true);

    return this.authApi.apiV1AuthActiveBranchPost({ changeActiveBranchDto: { branchId } }).pipe(
      tap((response) => {
        const data = response.data;
        if (!data?.token) throw new Error('El cambio de sede no devolvió un token nuevo.');

        this.authService.replaceAccessToken(data.token);
        this.authService.applyBranchSession(data.activeBranchId ?? branchId, data.branchIds ?? []);
      }),
      map(() => void 0),
      catchError((error: unknown) => {
        this._isSwitching.set(false);
        return throwError(() => error);
      }),
      tap({ next: () => this._isSwitching.set(false) })
    );
  }

  /** Recarga el catálogo de nombres, p. ej. tras crear o renombrar una sede. */
  reloadCatalog(): void {
    this.catalogRs.reload();
  }
}
